import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LogOut, User, Menu, X, ChevronDown,
  Bookmark, ClipboardList, Sparkles, GraduationCap, Building,
  LayoutDashboard, Briefcase, FileText,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useViewMode } from '../../contexts/ViewModeContext';
import { useToast } from '../../contexts/ToastContext';
import Button from '../ui/Button';
import LogoIcon from '../ui/LogoIcon';

const NAV_LINKS_GLOBAL = [
  { to: '/about',  label: 'About Us' },
  { to: '/events', label: 'Events' },
  { to: '/blog',   label: 'Blog' },
];

const Header: React.FC = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { viewMode, setViewMode } = useViewMode();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled,        setScrolled]        = useState(false);
  const [mobileOpen,      setMobileOpen]      = useState(false);
  const [userMenuOpen,    setUserMenuOpen]     = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  /* Close mobile menu on route change */
  useEffect(() => { setMobileOpen(false); setUserMenuOpen(false); }, [location.pathname]);

  /* Scroll shadow */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* Close user dropdown on outside click */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    if (userMenuOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [userMenuOpen]);

  const handleLogout = async () => {
    const ok = window.confirm('Are you sure you want to log out?');
    if (!ok) return;
    try {
      await logout();
      toast('Logged out successfully', 'success');
      navigate('/');
    } catch {
      /* swallow */
    }
  };

  const navLinkCls = (to: string) =>
    `text-sm font-medium transition-colors duration-200 relative group ${
      isActive(to)
        ? 'text-primary-600'
        : 'text-slate-600 hover:text-slate-900'
    }`;

  const MobileLink: React.FC<{ to: string; children: React.ReactNode; icon?: React.ReactNode }> = ({
    to, children, icon,
  }) => (
    <Link
      to={to}
      className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
        isActive(to)
          ? 'bg-primary-50 text-primary-700'
          : 'text-slate-700 hover:bg-slate-50'
      }`}
    >
      {icon && <span className="text-slate-400">{icon}</span>}
      {children}
    </Link>
  );

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/90 backdrop-blur-xl shadow-sm border-b border-slate-200'
          : 'bg-white/80 backdrop-blur-md border-b border-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          {/* ── Logo ── */}
          <button
            onClick={() => { setViewMode('agnostic'); navigate('/'); }}
            className="flex items-center gap-2.5 group flex-shrink-0"
          >
            <div className="transition-transform duration-300 group-hover:scale-105">
              <LogoIcon className="h-7 w-7 text-primary-600" />
            </div>
            <div className="flex flex-col items-start">
              <div className="flex items-center gap-1.5">
                <span className="text-lg font-extrabold tracking-tight text-slate-900">Linzara</span>
                <span className="hidden sm:inline-block text-xs font-semibold text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-2 py-0.5">
                  Beta
                </span>
              </div>
              <span className="hidden lg:block text-[10px] text-slate-400 whitespace-nowrap font-medium leading-none">
                Visa-aware hiring, made clear and simple
              </span>
            </div>
          </button>

          {/* ── Desktop nav + auth (grouped right) ── */}
          <div className="hidden md:flex items-center gap-5">

            {/* View toggle — unauthenticated only */}
            {!isAuthenticated && (
              <div className="flex items-center bg-slate-100 rounded-lg p-0.5 gap-0.5">
                <button
                  onClick={() => { setViewMode('candidate'); navigate('/'); }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-200 ${
                    viewMode === 'candidate'
                      ? 'bg-white text-primary-700 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <GraduationCap className="h-3.5 w-3.5" />
                  Candidate
                </button>
                <button
                  onClick={() => { setViewMode('employer'); navigate('/'); }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-200 ${
                    viewMode === 'employer'
                      ? 'bg-white text-primary-700 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <Building className="h-3.5 w-3.5" />
                  Employer
                </button>
              </div>
            )}

            {/* Student nav */}
            {isAuthenticated && user?.role === 'student' && (
              <>
                <Link to="/career" className={navLinkCls('/career')}>
                  Analyse
                </Link>
                <Link to="/saved" className={navLinkCls('/saved')}>
                  Saved
                </Link>
                <Link to="/career/assistant" className={`${navLinkCls('/career/assistant')} flex items-center gap-1.5`}>
                  <Sparkles className="h-3.5 w-3.5" />
                  Coach
                </Link>
              </>
            )}

            {/* Employer nav */}
            {isAuthenticated && user?.role === 'employer' && (
              <>
                {[
                  { to: '/employer/post-job',     label: 'Post a Job' },
                  { to: '/employer/dashboard',    label: 'Dashboard' },
                  { to: '/employer/applications', label: 'Applications' },
                  { to: '/employer/drafts',       label: 'Drafts' },
                ].map(({ to, label }) => (
                  <Link key={to} to={to} className={navLinkCls(to)}>
                    {label}
                    {isActive(to) && (
                      <span className="absolute -bottom-0.5 left-0 w-full h-0.5 bg-primary-600 rounded-full" />
                    )}
                  </Link>
                ))}
              </>
            )}

            {/* Global links — public pages, only when not logged in */}
            {!isAuthenticated && NAV_LINKS_GLOBAL.map(({ to, label }) => (
              <Link key={to} to={to} className={navLinkCls(to)}>
                {label}
              </Link>
            ))}

            {!isAuthenticated && (
              <a
                href="mailto:manav@linzara.com"
                className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors duration-200"
              >
                Contact
              </a>
            )}

            {/* Divider between nav and auth */}
            <div className="w-px h-4 bg-slate-200" />

            {!isAuthenticated && (
              <Link to="/auth">
                <Button variant="ghost" size="sm">Sign In</Button>
              </Link>
            )}

            {isAuthenticated ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(v => !v)}
                  className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-xl border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all duration-200 text-sm"
                >
                  <div className="h-7 w-7 rounded-full bg-gradient-to-br from-primary-500 to-teal-400 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {(user?.full_name || user?.email || 'U').charAt(0).toUpperCase()}
                  </div>
                  <span className="font-medium text-slate-700 max-w-[120px] truncate">
                    {user?.full_name?.split(' ')[0] || user?.email}
                  </span>
                  <ChevronDown
                    className={`h-3.5 w-3.5 text-slate-400 transition-transform duration-200 ${
                      userMenuOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                <AnimatePresence>
                  {userMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: -4 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -4 }}
                      transition={{ duration: 0.15, ease: 'easeOut' }}
                      className="absolute right-0 mt-2 w-64 bg-white border border-slate-200 rounded-2xl shadow-xl py-2 z-50"
                    >
                      {/* User info */}
                      <div className="px-4 py-3 border-b border-slate-100">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-primary-500 to-teal-400 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                            {(user?.full_name || user?.email || 'U').charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-900 truncate">{user?.full_name || 'User'}</p>
                            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                          </div>
                        </div>
                      </div>

                      <div className="py-1">
                        {user?.role === 'student' && (
                          <>
                            <Link
                              to="/career/assistant"
                              className="flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                              onClick={() => setUserMenuOpen(false)}
                            >
                              <Sparkles className="h-4 w-4 text-teal-500" /> Career Coach
                            </Link>
                            <span className="flex items-center gap-2.5 px-4 py-2 text-sm text-slate-400 cursor-not-allowed">
                              <Bookmark className="h-4 w-4" /> My Saved Jobs
                            </span>
                            <span className="flex items-center gap-2.5 px-4 py-2 text-sm text-slate-400 cursor-not-allowed">
                              <ClipboardList className="h-4 w-4" /> My Applications
                            </span>
                          </>
                        )}

                        <Link
                          to="/profile"
                          className="flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <User className="h-4 w-4 text-slate-400" /> Profile
                        </Link>

                        {user?.role === 'employer' && (
                          <>
                            {[
                              { to: '/employer/post-job',    icon: <Briefcase className="h-4 w-4 text-slate-400" />,    label: 'Post a Job' },
                              { to: '/employer/applications',icon: <ClipboardList className="h-4 w-4 text-slate-400" />, label: 'Applications' },
                              { to: '/employer/dashboard',   icon: <LayoutDashboard className="h-4 w-4 text-slate-400" />,label: 'Dashboard' },
                              { to: '/employer/drafts',      icon: <FileText className="h-4 w-4 text-slate-400" />,       label: 'Drafts' },
                            ].map(({ to, icon, label }) => (
                              <Link
                                key={to}
                                to={to}
                                className="flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                                onClick={() => setUserMenuOpen(false)}
                              >
                                {icon} {label}
                              </Link>
                            ))}
                          </>
                        )}

                        {/* Recommendations placeholder */}
                        <div className="px-4 py-2 mt-1 border-t border-slate-100">
                          <div className="flex items-center gap-2 text-xs text-slate-400">
                            <Sparkles className="h-3.5 w-3.5" />
                            {user?.role === 'employer' ? 'Candidate matches' : 'Job recommendations'} — coming soon
                          </div>
                        </div>
                      </div>

                      <div className="px-3 pt-1 pb-2 border-t border-slate-100">
                        <button
                          onClick={() => { setUserMenuOpen(false); handleLogout(); }}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-xl transition-colors"
                        >
                          <LogOut className="h-4 w-4" /> Log out
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                {viewMode === 'employer' ? (
                  <Link to="/request-demo">
                    <Button size="sm">Request Demo</Button>
                  </Link>
                ) : (
                  <Link to="/early-access">
                    <Button variant="outline" size="sm">Beta Waitlist</Button>
                  </Link>
                )}
              </div>
            )}
          </div>

          {/* ── Mobile hamburger ── */}
          <button
            onClick={() => setMobileOpen(v => !v)}
            className="md:hidden p-2 rounded-xl text-slate-600 hover:bg-slate-100 transition-colors"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={mobileOpen ? 'close' : 'open'}
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0,   opacity: 1 }}
                exit={{   rotate:  90, opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="flex items-center"
              >
                {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </motion.span>
            </AnimatePresence>
          </button>
        </div>
      </div>

      {/* ── Mobile menu ── */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{   opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="md:hidden overflow-hidden border-t border-slate-100 bg-white"
          >
            <div className="px-4 py-4 space-y-1 max-h-[75vh] overflow-y-auto">
              {/* View toggle mobile */}
              {!isAuthenticated && (
                <div className="flex bg-slate-100 rounded-xl p-1 gap-1 mb-3">
                  {[
                    { mode: 'candidate' as const, icon: <GraduationCap className="h-4 w-4" />, label: 'Candidate' },
                    { mode: 'employer'  as const, icon: <Building      className="h-4 w-4" />, label: 'Employer'  },
                  ].map(({ mode, icon, label }) => (
                    <button
                      key={mode}
                      onClick={() => { setViewMode(mode); navigate('/'); }}
                      className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold transition-all ${
                        viewMode === mode
                          ? 'bg-white text-primary-700 shadow-sm'
                          : 'text-slate-500'
                      }`}
                    >
                      {icon} {label}
                    </button>
                  ))}
                </div>
              )}

              {isAuthenticated && user?.role === 'student' && (
                <>
                  <MobileLink to="/career" icon={<LayoutDashboard className="h-4 w-4" />}>Analyse</MobileLink>
                  <MobileLink to="/career/assistant" icon={<Sparkles className="h-4 w-4" />}>
                    Coach
                  </MobileLink>
                  <MobileLink to="/saved" icon={<Bookmark className="h-4 w-4" />}>
                    Saved
                  </MobileLink>
                </>
              )}

              {isAuthenticated && user?.role === 'employer' && (
                <>
                  <MobileLink to="/employer/post-job"    icon={<Briefcase      className="h-4 w-4" />}>Post a Job</MobileLink>
                  <MobileLink to="/employer/dashboard"   icon={<LayoutDashboard className="h-4 w-4" />}>Dashboard</MobileLink>
                  <MobileLink to="/employer/applications"icon={<ClipboardList  className="h-4 w-4" />}>Applications</MobileLink>
                  <MobileLink to="/employer/drafts"      icon={<FileText       className="h-4 w-4" />}>Drafts</MobileLink>
                </>
              )}

              {!isAuthenticated && (
                <>
                  {NAV_LINKS_GLOBAL.map(({ to, label }) => (
                    <MobileLink key={to} to={to}>{label}</MobileLink>
                  ))}
                  <a
                    href="mailto:manav@linzara.com"
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    Contact
                  </a>
                </>
              )}

              <div className="pt-3 mt-3 border-t border-slate-100 space-y-2">
                {isAuthenticated ? (
                  <>
                    <div className="flex items-center gap-3 px-4 py-2">
                      <div className="h-8 w-8 rounded-full bg-gradient-to-br from-primary-500 to-teal-400 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                        {(user?.full_name || user?.email || 'U').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{user?.full_name || 'User'}</p>
                        <p className="text-xs text-slate-400">{user?.email}</p>
                      </div>
                    </div>
                    <MobileLink to="/profile" icon={<User className="h-4 w-4" />}>Profile</MobileLink>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <LogOut className="h-4 w-4" /> Log out
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/auth" className="block">
                      <Button variant="ghost" className="w-full" size="md">Sign In</Button>
                    </Link>
                    {viewMode === 'employer' ? (
                      <Link to="/request-demo" className="block">
                        <Button className="w-full" size="md">Request Demo</Button>
                      </Link>
                    ) : (
                      <Link to="/early-access" className="block">
                        <Button variant="outline" className="w-full" size="md">Beta Waitlist</Button>
                      </Link>
                    )}
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
